<template>
    <component :is="layout" ref="layout"/>
</template>

<script>
import {defineAsyncComponent} from 'vue'
import store from "@/store"
import { $session } from "@/plugin/dot-framework-vue-1.0.8"

/**
 * 폰트 설정
 */
import '@/asset/css/font.css';
import FontFaceObserver from 'fontfaceobserver';
import "@/asset/css/global.scss"

/**
 * 레이아웃 설정
 */
// apps
const DefaultLayout = defineAsyncComponent(() => import("@/layout/apps/DefaultLayout.vue"))
const CloudLayout = defineAsyncComponent(() => import("@/layout/apps/CloudLayout.vue"))

// easy
const EasyCloudLayout = defineAsyncComponent(() => import("@/layout/easy/EasyCloudLayout.vue"))

// common
const WideLayout = defineAsyncComponent(() => import("@/layout/WideLayout.vue"))
const BlankLayout = defineAsyncComponent(() => import("@/layout/BlankLayout.vue"))
const EmbeddedBookLayout = defineAsyncComponent(() => import("@/layout/EmbeddedBookLayout.vue"))

export default {
    name: 'App',
    components: {
        DefaultLayout,
        BlankLayout,
        EmbeddedBookLayout,
        WideLayout,
        CloudLayout,
        EasyCloudLayout
    },
    provide() {
        return {
            dotFilter: this.$filter,
            dotMixins: this.$mixins
        }
    },
    data() {
        return {
            layout: null,
            convertCheckInterval: null,
        }
    },
    created() {
        this.setFont();
        this.setHTMLLangAttribute()
        this.initLocale()
    },
    beforeMount() {
        document.getElementById("spinner").classList.add('d-none');
    },
    watch: {
        $route(to) {
            if (to.meta.layout !== undefined) {
              this.layout = to.meta.layout
            } else {
              this.layout = "User"
            }

            if (this.$store.getters["auth/isAuthenticated"]) {
                if (this.convertCheckInterval) {
                    clearInterval(this.convertCheckInterval);
                }
                this.convertCheckInterval = setInterval(() => {
                    if (this.checkPageVisibility()) {
                      this.loadAndShowPopups();
                    }
                }, 30000);
            } else {
                if (this.convertCheckInterval) {
                    clearInterval(this.convertCheckInterval);
                    this.convertCheckInterval = null;
                }
            }
        },
        '$store.getters["auth/isAuthenticated"]'(isAuthenticated) {
            if (!isAuthenticated) {
                if (this.convertCheckInterval) {
                    clearInterval(this.convertCheckInterval);
                    this.convertCheckInterval = null;
                }
            }
        },
        '$i18n.locale'() {
            this.setHTMLLangAttribute();
            // this.setHTMLDirAttribute();
        },
    },
    beforeDestroy() {
        // 컴포넌트가 파괴되기 전에 인터벌 클리어
        if (this.convertCheckInterval) {
            clearInterval(this.convertCheckInterval)
        }
    },
    methods: {
        setHTMLDirAttribute() {
            document.documentElement.setAttribute('dir', this.$i18n.locale === 'ar' ? 'rtl' : 'auto')
        },
        /**
         * 다국어 설정
         */
        setHTMLLangAttribute() {
            document.documentElement.setAttribute('lang', this.$i18n.locale)
        },
        initLocale() {
            const defaultLanguage = "en"

            // 1) 쿠키에 저장된 사용자 선택 언어가 있으면 우선 적용
            const cookieLocale = this.$cookies.get("user_locale")
            if (cookieLocale && this.$i18n.availableLocales.includes(cookieLocale)) {
                return this.$i18n.locale = cookieLocale
            }

            // 2) 쿠키가 없으면 브라우저 언어 (백엔드 IP 기반 결정값이 별도 설정한 경우는 그 값을 쿠키에 심어주는 것을 가정)
            const lang = navigator.language || navigator.userLanguage
            const code = lang.substring(0, 2)
            const availableLocales = this.$i18n.availableLocales.includes(code)

            return this.$i18n.locale = availableLocales ? code : defaultLanguage
        },
        /**
         * 폰트 설정
         * @see https://fonts.google.com/knowledge/glossary/fout
         */
        setFont() {
            const font = new FontFaceObserver('Helvetica Neue')

            font.load().then(function () {
                document.body.classList.add("fonts-loaded");
            }).catch(function () {
                console.log('Helvetica Neue failed to load.');
            })
        },

        async loadAndShowPopups() {
            try {
                const popupList = await store.dispatch("textbook/getConversionCompletePopup", $session.getUserNo())
                let message = ""

                if (popupList && popupList.length > 0) {
                    for (const popup of popupList) {
                        await store.dispatch("textbook/popupDisplayComplete", popup.DTM_NO)
                        if (popup.STATUS === "FAILED") {
                            message = this.$t('HTML 변환에 실패하였습니다.')
                        } else {
                            message = this.$t('HTML 변환이 완료되었습니다.')
                        }
                    }

                    this.showPopup(message);
                }
            } catch (error) {
                console.error("Failed to load and show popups:", error)
            }
        },
        showPopup(message) {
            const Toast = this.$swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            });

            Toast.fire({
                html: `<swalCustomTag style="color: black;"><b>${message}</b></swalCustomTag>`,
            });
        },
        checkPageVisibility() {
            return document.visibilityState === "visible"
        }
    }
}
</script>

<style scoped>
</style>
